import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { Dialog } from 'vnetwork-ui'
import { Loading } from 'vnetwork-ui'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import { useDataAccessPreview } from '../../hooks/useDataAccessPreview'
import PreviewResult from './PreviewResult'

interface TestAsUserModalProps {
    open: boolean
    onClose: () => void
    ruleId: number
}

interface FormValues {
    employee_id: number | null
}

const TestAsUserModal: React.FC<TestAsUserModalProps> = ({
    open,
    onClose,
    ruleId,
}) => {
    const { t } = useTranslation('settings')
    const [employeeId, setEmployeeId] = useState<number | null>(null)

    const formControl = useForm<FormValues>({
        defaultValues: { employee_id: null },
    })
    const { control, handleSubmit, watch, reset } = formControl
    const selectedId = watch('employee_id')
    const hasSelection = selectedId != null

    const { data, isLoading, isError, error } = useDataAccessPreview(
        ruleId,
        employeeId
    )
    const preview = data?.result ?? null

    const handleClose = () => {
        setEmployeeId(null)
        reset({ employee_id: null })
        onClose()
    }

    const onSubmit = (values: FormValues) => {
        setEmployeeId(values.employee_id ?? null)
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            title={t('data-access-preview-modal-title')}
            maxWidth="sm"
            closeOnClick={false}
        >
            <div className="p-4 space-y-4">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                    <EmployeeSelect
                        control={control}
                        name="employee_id"
                        multiple={false}
                        label="data-access-preview-select-employee"
                        showLabel
                    />
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={handleSubmit(onSubmit)}
                            disabled={!hasSelection}
                            className={
                                hasSelection
                                    ? 'px-4 py-1.5 rounded text-sm font-medium bg-primary text-white hover:bg-primary/90'
                                    : 'px-4 py-1.5 rounded text-sm font-medium bg-muted text-muted-foreground cursor-not-allowed'
                            }
                        >
                            {t('data-access-preview-submit')}
                        </button>
                    </div>
                </form>

                {isLoading && (
                    <div className="flex justify-center py-6">
                        <Loading.FadingDots />
                    </div>
                )}

                {isError && !isLoading && (
                    <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                        {(error as any)?.message ?? t('something-went-wrong')}
                    </div>
                )}

                {!isLoading && !isError && preview && (
                    <PreviewResult data={preview} />
                )}
            </div>
        </Dialog>
    )
}

export default TestAsUserModal
